/**
 * cells/CurrentEpcCell.tsx
 *
 * Shows the current EPC label chip with hover tooltip for actions
 * (open document, edit metadata, delete).
 */
import { Tooltip as HeroTooltip } from "@heroui/react";
import { Plus, Upload, Trash } from "@phosphor-icons/react";
import {
  EPCData,
  EPCLabel,
  getEPCColor,
  getDisplayLabel,
} from "shared/components/maintenance/hooks/useEnergyPerformanceData";
import { isSpecificEPCIncomplete, formatDate } from "./utils";

interface CurrentEpcCellProps {
  unit: EPCData;
  onEdit: (fileLogId: number, unit: EPCData) => void;
  onDelete: (
    fileLogId: number,
    unitLocation: string,
    epcLabel?: string,
    documentDate?: string,
    kWh?: string,
  ) => void;
  onUpload: (buildingId?: string, unitId?: string) => void;
}

function EpcUploadButton({
  unit,
  onUpload,
}: {
  unit: EPCData;
  onUpload: (buildingId?: string, unitId?: string) => void;
}) {
  const unitIdArg =
    unit.unitId !== `${unit.buildingId}-gemeenschap` ? unit.unitId : undefined;
  return (
    <button
      onClick={() => onUpload(unit.buildingId, unitIdArg)}
      className="bg-brick-primary hover:bg-brick-primary/90 flex items-center gap-2 rounded-md px-3 py-1 text-sm text-white transition-colors"
    >
      <Upload size={16} />
      Laad EPC op
    </button>
  );
}

function EpcStatusAction({
  unit,
  onUpload,
}: {
  unit: EPCData;
  onUpload: (buildingId?: string, unitId?: string) => void;
}) {
  const hasEPC =
    unit.currentEPC && unit.currentEPC.label !== EPCLabel.ONTBREKEND;
  const unitIdArg =
    unit.unitId !== `${unit.buildingId}-gemeenschap` ? unit.unitId : undefined;

  if (hasEPC) {
    return (
      <div className="flex items-center gap-2">
        <span className="text-status-good text-sm font-medium">
          EPC opgeladen
        </span>
        <button
          onClick={() => onUpload(unit.buildingId, unitIdArg)}
          className="text-neutral-secondary-mid hover:text-neutral-primary p-1 transition-colors"
          title="Nieuwe EPC toevoegen"
        >
          <Plus size={16} />
        </button>
      </div>
    );
  }

  return <EpcUploadButton unit={unit} onUpload={onUpload} />;
}

export function CurrentEpcCell({
  unit,
  onEdit,
  onDelete,
  onUpload,
}: CurrentEpcCellProps) {
  if (!unit.currentEPC)
    return <EpcUploadButton unit={unit} onUpload={onUpload} />;

  const isIncomplete = isSpecificEPCIncomplete(unit.currentEPC);
  const hasFileUrl =
    unit.currentEPC.fileLogId && unit.currentEPC.sharePointFileUrl;

  const tooltipContent = (
    <div className="flex flex-col gap-2 p-2">
      <div className="text-sm font-medium">
        {unit.currentEPC.label}
        {unit.currentEPC.documentDate && (
          <span className="ml-2 text-xs text-gray-600">
            - {formatDate(unit.currentEPC.documentDate)}
          </span>
        )}
      </div>
      {unit.currentEPC.kWh && (
        <div className="text-xs text-gray-600">
          {unit.currentEPC.kWh} kWh/m²/jaar
        </div>
      )}
      <div className="mt-2 flex gap-2">
        {hasFileUrl && (
          <a
            href={unit.currentEPC.sharePointFileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-moss-primary hover:bg-moss-primary/90 rounded-sm px-2 py-1 text-xs text-white transition-colors"
          >
            Open EPC
          </a>
        )}
        {unit.currentEPC.fileLogId && (
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (unit.currentEPC?.fileLogId) {
                onEdit(unit.currentEPC.fileLogId, unit);
              }
            }}
            className={`rounded px-2 py-1 text-xs transition-colors ${
              isIncomplete
                ? "bg-yellow-500 text-white hover:bg-yellow-600"
                : "bg-neutral-primary hover:bg-neutral-primary/90 text-white"
            }`}
          >
            {isIncomplete ? "EPC data aanvullen" : "Bewerk EPC data"}
          </button>
        )}
        {unit.currentEPC.fileLogId && (
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (unit.currentEPC?.fileLogId) {
                onDelete(
                  unit.currentEPC.fileLogId,
                  unit.location,
                  unit.currentEPC.label,
                  unit.currentEPC.documentDate,
                  unit.currentEPC.kWh,
                );
              }
            }}
            className="flex items-center gap-1 rounded-sm bg-red-500 px-2 py-1 text-xs text-white transition-colors hover:bg-red-600"
          >
            <Trash size={12} />
            Verwijder EPC
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <HeroTooltip
          content={tooltipContent}
          placement="top"
          classNames={{
            content: "p-0 bg-white border border-neutral-primary/20 shadow-lg",
          }}
        >
          <span
            className={`cursor-pointer rounded-lg px-3 py-1 font-bold transition-opacity hover:opacity-80 ${getEPCColor(unit.currentEPC.label)} ${
              isIncomplete ? "ring-2 ring-yellow-400" : ""
            }`}
          >
            {getDisplayLabel(unit.currentEPC.label)}
          </span>
        </HeroTooltip>
        {unit.currentEPC.kWh && (
          <span className="text-neutral-secondary-mid text-xs">
            {unit.currentEPC.kWh} kWh/m²
          </span>
        )}
      </div>
      <EpcStatusAction unit={unit} onUpload={onUpload} />
    </div>
  );
}
